import { useState } from 'react'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import { useGetDormakabaCommonAreas } from '@/api/keyless/dormakaba/get-dormakaba-access-points'
import {
  getDormakabaIsConnectedQueryKey,
  useGetDormakabaIsConnected,
} from '@/api/keyless/dormakaba/get-dormakaba-is-connected'
import { useUpdateDormakabaUser } from '@/api/keyless/dormakaba/update-dormakaba-user'
import { useIsDormakabaBuildingConnected } from '@/api/property-keyless/dormakaba/use-is-dormakaba-building-connected'
import { Alert } from '@/components/ui/alert'
import { resetQueries } from '@/utils'

import API from '../../../../api/api'
import { getDormakabaActivationErrorMessage } from '../../../../api/keyless/dormakaba/get-dormakaba-activation-error-message'
import Loader from '../../../../components/Loader/Loader'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { saltoSpaceDropdownStyles } from '../helpers'

const DormakabaSection = ({
  isAvailable,
  isSubmitting,
  buildingId,
  clientId,
  clientEmail,
  apartment,
}) => {
  const { t } = useTranslation(['clients-legacy', 'clients'])
  const [isLoading, setLoading] = useState(false)
  const [selectedCommonAreas, setSelectedCommonAreas] = useState([])
  const { displaySnackbar } = useSnackbar()
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsDormakabaBuildingConnected(buildingId)
  const {
    data: commonAreas = [],
    isError: isCommonAreasError,
    isLoading: isLoadingCommonAreas,
  } = useGetDormakabaCommonAreas({ buildingId }, { enabled: isAvailable && isBuildingConnected })
  const { data: userConnection, isLoading: isLoadingUserConnection } = useGetDormakabaIsConnected({
    email: isAvailable ? clientEmail : undefined,
  })
  const { mutateAsync: updateUser } = useUpdateDormakabaUser()
  const isUserConnected = userConnection?.isConnected === true

  const activate = async () => {
    const commonAreaIds = selectedCommonAreas.map((area) => area.commonAreaId)
    await API.createKeylessAccessDormakabaKey({
      clientId,
      commonAreaIds: commonAreaIds.length > 0 ? commonAreaIds : undefined,
    })
    await resetQueries(getDormakabaIsConnectedQueryKey({ email: clientEmail }))
    displaySnackbar(t('clients-legacy:profileView.integrations.dormakaba.activationSuccess'))
  }

  const update = async () => {
    await updateUser({
      commonAreas: selectedCommonAreas.map((area) => ({ commonAreaID: String(area.commonAreaId) })),
      email: clientEmail,
    })
    displaySnackbar(t('clients:keylessAccess.dormakaba.toasts.updateSuccess'))
  }

  const onSubmit = async () => {
    if (!clientId) {
      displaySnackbar('Cannot activate: missing client identifier.', true)
      return
    }
    setLoading(true)
    try {
      await (isUserConnected ? update() : activate())
      setSelectedCommonAreas([])
    } catch (e) {
      const rawMessage = e?.response?.data?.message
      const fallbackMessage =
        (typeof rawMessage === 'string' && rawMessage) ||
        (typeof rawMessage?.error === 'string' && rawMessage.error) ||
        t('clients-legacy:profileView.integrations.dormakaba.activationError')
      displaySnackbar(getDormakabaActivationErrorMessage(e, fallbackMessage), true)
    } finally {
      setLoading(false)
    }
  }

  if (!isAvailable) return null

  if (isLoadingBuildingConnection) {
    return (
      <div className="information-element">
        <Loader />
      </div>
    )
  }

  if (!isBuildingConnected) {
    return (
      <div className="information-element">
        <Alert
          type="error"
          title={t('clients:keylessAccess.dormakaba.buildingNotConnected.title')}
          message={t('clients:keylessAccess.dormakaba.buildingNotConnected.message')}
        />
      </div>
    )
  }

  return (
    <div className="information-element">
      <div className="columns">
        <div className="description-column">
          <div className="element-name element-margin-top">
            {t('clients-legacy:profileView.integrations.dormakaba.label')}
          </div>

          <div className="dropdown-buttons-container">
            <div className="dropdown-buttons-container-row">
              <div className="dropdown-container">
                <div className="dropdown-header">
                  {t('clients:keylessAccess.dormakaba.unit.label')}
                </div>
                <input
                  name="dormakaba-unit"
                  className="dropdown-button"
                  style={{ cursor: 'default' }}
                  placeholder={t('clients:keylessAccess.dormakaba.unit.placeholder')}
                  value={apartment || ''}
                  disabled
                  readOnly
                />
              </div>
              <div className="dropdown-container">
                <div className="dropdown-header">
                  {t('clients:keylessAccess.dormakaba.commonAreas.label')}
                </div>
                <Select
                  styles={saltoSpaceDropdownStyles(isCommonAreasError)}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  options={commonAreas}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => String(option.commonAreaId)}
                  value={selectedCommonAreas}
                  onChange={(selectedOptions) => setSelectedCommonAreas(selectedOptions || [])}
                  placeholder={t('clients:keylessAccess.dormakaba.commonAreas.placeholder')}
                  isLoading={isLoadingCommonAreas}
                  isDisabled={isLoading}
                  isClearable
                  isMulti
                  closeMenuOnSelect={false}
                />
                {isCommonAreasError && (
                  <div className="error-validation">
                    {t('clients:keylessAccess.dormakaba.commonAreas.loadError')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            className={`confirm-button button-primary button ${isLoading ? 'is-loading' : ''}`}
            onClick={onSubmit}
            disabled={
              isSubmitting ||
              isLoading ||
              isLoadingCommonAreas ||
              isLoadingUserConnection ||
              !apartment
            }
          >
            {isUserConnected
              ? t('clients:keylessAccess.dormakaba.update')
              : t('clients:keylessAccess.dormakaba.activate')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DormakabaSection
